import React, { useState } from "react";
import { getDataSource } from "../../services/api";
import {
  formatGermanNumber,
  handleImageError,
  imageNameFormat,
} from "../../utils/common";
import BarVeriticalElections from "../Charts/BarVeriticalElections";
import Map from "../Map/Map";
import {
  Legend,
  LegendAdr,
  LegendCsv,
  LegendDeigreng,
  LegendDeikonservativ,
  LegendDeilenk,
  LegendDp,
  LegendKpl,
  LegendLsap,
  LegendPiraten,
} from "../../assets";

function getUniqueYears(data) {
  const uniqueYears = [];

  data.forEach((item) => {
    if (!uniqueYears.includes(item.year)) {
      uniqueYears.push(item.year);
    }
  });

  return uniqueYears.reverse();
}

const Result = ({ t, data, language }) => {
  const { partyName, elections, heatmap, logo } = data;

  const directions = getUniqueYears(elections);
  const [selectedTab, setSelectedTab] = useState(directions[0]);

  const selectedYear = elections.find(
    (election) => election.year === selectedTab
  );

  const firstCommentKey = "firstTopCandidateComment" + language.toUpperCase();
  const seccondCommentKey = "secondCandidateComment" + language.toUpperCase();

  const currentHeatmap = heatmap.filter((item) => item.year === selectedTab);

  const hasTopCandidate =
    selectedYear.topCandidateName === undefined ? false : true;

  const buildImageUrl = (name) => {
    if (!name) return "";
    const imageName = imageNameFormat(name.split(" ")[0], name.split(" ")[1]);
    return `${imageName}_${selectedTab}.webp`;
  };

  const topCandidateImageUrl = buildImageUrl(selectedYear.topCandidateName);
  const hasTwoCandidates = selectedYear.topCandidateName?.indexOf("/") > 0;
  const isDE = language === "de";

  const renderLegend = () => {
    switch (logo) {
      case "adr":
        return <LegendAdr className="mr-4" />;
      case "csv":
        return <LegendCsv className="mr-4" />;
      case "deigreng":
        return <LegendDeigreng className="mr-4" />;
      case "deikonservativ":
        return <LegendDeikonservativ className="mr-4" />;
      case "deilenk":
        return <LegendDeilenk className="mr-4" />;
      case "dp":
        return <LegendDp className="mr-4" />;
      case "kpl":
        return <LegendKpl className="mr-4" />;
      case "lsap":
        return <LegendLsap className="mr-4" />;
      case "piraten":
        return <LegendPiraten className="mr-4" />;
      default:
        return <Legend className="mr-4" />;
    }
  };

  const renderContent = (party) => {
    const getMaxValue = () => {
      if (!currentHeatmap.length) {
        return [0, 0];
      }

      let minMax = currentHeatmap.reduce(
        (acc, item) => {
          if (typeof item.votePercentage === "number") {
            if (item.votePercentage < acc.min) {
              acc.min = item.votePercentage;
            }
            if (item.votePercentage > acc.max) {
              acc.max = item.votePercentage;
            }
          }
          return acc;
        },
        { min: Infinity, max: -Infinity }
      );

      // If there were no valid numbers found, default to 0,0
      if (minMax.min === Infinity) minMax.min = 0;
      if (minMax.max === -Infinity) minMax.max = 0;

      return [minMax.min, minMax.max];
    };

    return (
      <div className="">
        <span className="text-22 font-medium">
          {t("stronghold") + " " + party}
        </span>
        <div className="flex flex-row mt-4">
          <div className="w-13 h-[170px] flex flex-col items-center justify-between">
            <div className="text-sm">{`${
              isDE ? formatGermanNumber(getMaxValue()[1]) : getMaxValue()[1]
            }%`}</div>
            {renderLegend()}
            <div className="text-sm">{`${
              isDE ? formatGermanNumber(getMaxValue()[0]) : getMaxValue()[0]
            }%`}</div>
          </div>

          <Map
            data={currentHeatmap}
            party={logo}
            t={t}
            year={selectedTab}
            isDe={isDE}
          />
        </div>
      </div>
    );
  };

  return (
    <div className=" bg-white py-20" id="results">
      <div className="px-0">
        <span className="text-22 font-medium md:text-32">{t("results")}</span>
        <div className="h-0.5 bg-black bg-opacity-40 mb-7"></div>
        <div className="flex">
          {directions.map((direction) => (
            <button
              key={direction}
              onClick={() => setSelectedTab(direction)}
              className={`
    py-2 mr-3 rounded h-12 w-20 md:w-32 
    ${
      selectedTab === direction
        ? "bg-primary text-white"
        : "bg-wort text-black hover:border-primary hover:text-primary"
    }
    hover:border-2 border-transparent transition-all duration-300
  `}
            >
              {direction}
            </button>
          ))}
        </div>
      </div>
      <div className="py-5 flex flex-col md:flex-row md:px-5">
        <div className="px-5 pb-5">{renderContent(partyName)}</div>
        <div className="mt-5 md:ml-5">
          <div className="bg-wort px-5">
            <div className="py-3 md:px-4 flex flex-col md:flex-row">
              {hasTopCandidate && !hasTwoCandidates && (
                <img
                  src={`${getDataSource()}/top-candidates/${topCandidateImageUrl}`}
                  alt={partyName}
                  onError={handleImageError}
                  width={93}
                  className="hidden md:block"
                />
              )}
              {!hasTopCandidate && (
                <img
                  src={`${getDataSource()}/top-candidates/${
                    logo + "_" + selectedTab
                  }.webp`}
                  alt={partyName}
                  onError={handleImageError}
                  width={93}
                  className="hidden md:block"
                />
              )}
              {hasTwoCandidates && (
                <div className="flex flex-row items-center justify-between w-full px-10">
                  <div className="flex flex-col justify-center items-center">
                    <img
                      src={`${getDataSource()}/top-candidates/${buildImageUrl(
                        selectedYear.topCandidateName.split("/")[0]
                      )}`}
                      alt={partyName}
                      onError={handleImageError}
                      width={93}
                      className="hidden md:block"
                    />
                    <span className="text-lg font-medium">
                      {hasTopCandidate
                        ? selectedYear.topCandidateName.split("/")[0]
                        : t("noTopCandidate")}
                    </span>
                    {hasTopCandidate && (
                      <span className="text-sm">
                        {selectedYear.topCandidateAge.split("/")[0] +
                          " " +
                          t("years")}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col justify-center items-center">
                    <img
                      src={`${getDataSource()}/top-candidates/${buildImageUrl(
                        selectedYear.topCandidateName.split("/")[1]
                      )}`}
                      alt={partyName}
                      onError={handleImageError}
                      width={93}
                      className="hidden md:block"
                    />
                    <span className="text-lg font-medium">
                      {hasTopCandidate
                        ? selectedYear.topCandidateName.split("/")[1]
                        : t("noTopCandidate")}
                    </span>
                    <span className="text-sm">
                      {selectedYear.topCandidateAge.split("/")[1] +
                        " " +
                        t("years")}
                    </span>
                  </div>
                </div>
              )}

              {!hasTwoCandidates && (
                <div className="flex flex-col md:pl-4 justify-center">
                  <span className="text-xs">{t("topCandidate")}</span>
                  <span className="text-lg font-medium">
                    {hasTopCandidate
                      ? selectedYear.topCandidateName
                      : t("noTopCandidate")}
                  </span>

                  {hasTopCandidate && (
                    <span className="text-sm">
                      {t("ageAtThatTime")}
                      {selectedYear.topCandidateAge
                        ? selectedYear.topCandidateAge + " " + t("years")
                        : ""}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="h-0.5 bg-black bg-opacity-40 mb-2"></div>
            <div className="flex flex-col">
              <span className="mb-4">{selectedYear[firstCommentKey]}</span>
              <span className="mb-4">{selectedYear[seccondCommentKey]}</span>
            </div>
          </div>
          <div className="pt-4 px-5">
            <span className="text-lg font-medium mt-5">
              {t("voteShareAtThatTime") + selectedTab}
            </span>
            <div className="h-[250px] lg:h-[250px] pt-3">
              <BarVeriticalElections
                data={elections}
                className="h-full"
                t={t}
                selectedYear={selectedTab}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Result;
